import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { type CreateOrderAndOpenSocketConfig, createOrderAndOpenSocket } from "#/payment/handlers/basePaymentHandler";
import { useCartStore } from "#/store/cart";
import { useMainStore } from "#/store/main";
import { useTableStore } from "#/store/table";
import { useUserStore } from "#/store/user";

export interface CreateOrderConfigPage {
	kiosk_menu: boolean;
	resetDiscounts?: () => void;
}

export interface CreateOrderParams {
	paymentMethodId?: string;
	items: any[];
	combos: any[];
	comment: string;
	signal?: AbortSignal;
	locator?: string;
	promo_code?: string;
}

export function useCreateOrder() {
	const route = useRoute();
	const userStore = useUserStore();
	const storeCart = useCartStore();
	const storeMain = useMainStore();
	const tableStore = useTableStore();

	const { isButtonDisabled } = storeToRefs(storeCart);

	const getCreateOrderConfig = (configPage: CreateOrderConfigPage, paymentMethodId?: string, locator?: string): CreateOrderAndOpenSocketConfig | null => {
		const context = tableStore.isTableService ? "tableservice" : configPage.kiosk_menu ? "kiosk" : "qr";
		const result = storeCart.createPaymentHandler(context, configPage.kiosk_menu, paymentMethodId, locator);

		if (!result) return null;
		return {
			...result.config,
			paymentMethodId,
			route: { params: { orgId: String(route.params.orgId) } },
			handler: result.handler,
			resetDiscounts: configPage.resetDiscounts,
			setButtonDisabled: (value) => {
				isButtonDisabled.value = value;
			},
			on403Kiosk: () => {
				localStorage.removeItem("apiKey");
				storeMain.is_showApiKey = true;
			},
		};
	};

	const makeCreateOrderRequest = async (configPage: CreateOrderConfigPage, params: CreateOrderParams) => {
		const config = getCreateOrderConfig(configPage, params.paymentMethodId, params.locator);
		if (!config) return;
		return createOrderAndOpenSocket(
			{
				items: params.items || [],
				combos: params.combos || [],
				promo_code: params.promo_code || undefined,
			},
			config,
			{
				comment: params.comment,
				user: userStore.data,
				signal: params.signal,
			},
		);
	};

	return { getCreateOrderConfig, makeCreateOrderRequest };
}
